import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, Users, AlertTriangle, Plus, Trash2 } from 'lucide-react';
import { toast } from '@/hooks/use-toast';

const scheduleSchema = z.object({
  title: z.string().min(1, 'Schedule title is required'),
  description: z.string().optional(),
  startDate: z.string().min(1, 'Start date is required'),
  endDate: z.string().min(1, 'End date is required'),
  priority: z.enum(['low', 'medium', 'high', 'critical']),
  status: z.enum(['draft', 'scheduled', 'in_progress', 'completed', 'cancelled']),
  productionJobId: z.string().min(1, 'Production job is required'),
  workOrderIds: z.array(z.string()).min(1, 'At least one work order is required'),
  resources: z.array(z.object({
    resourceId: z.string().min(1, 'Resource is required'),
    resourceType: z.enum(['machine', 'operator', 'tool', 'material']),
    quantity: z.number().min(1, 'Quantity must be at least 1'),
    allocatedHours: z.number().min(0.1, 'Allocated hours must be greater than 0'),
    startTime: z.string().min(1, 'Start time is required'),
    endTime: z.string().min(1, 'End time is required'),
  })).min(1, 'At least one resource allocation is required'),
  shifts: z.array(z.object({
    shiftId: z.string().min(1, 'Shift is required'),
    date: z.string().min(1, 'Date is required'),
    startTime: z.string().min(1, 'Start time is required'),
    endTime: z.string().min(1, 'End time is required'),
    operatorIds: z.array(z.string()).min(1, 'At least one operator is required'),
  })).min(1, 'At least one shift is required'),
  constraints: z.object({
    maxConcurrentJobs: z.number().min(1, 'Max concurrent jobs must be at least 1'),
    bufferTime: z.number().min(0, 'Buffer time cannot be negative'),
    allowOvertime: z.boolean(),
    requireQualityCheck: z.boolean(),
  }),
  notes: z.string().optional(),
});

type ScheduleFormData = z.infer<typeof scheduleSchema>;

interface ScheduleFormProps {
  initialData?: Partial<ScheduleFormData>;
  onSubmit: (data: ScheduleFormData) => void;
  isLoading?: boolean;
}

// Mock data
const mockProductionJobs = [
  { id: '1', name: 'PJ-001 - Widget Assembly', status: 'pending' },
  { id: '2', name: 'PJ-002 - Component Manufacturing', status: 'in_progress' },
  { id: '3', name: 'PJ-003 - Quality Testing', status: 'scheduled' },
];

const mockWorkOrders = [
  { id: '1', code: 'WO-001', operation: 'Cutting', jobId: '1' },
  { id: '2', code: 'WO-002', operation: 'Assembly', jobId: '1' },
  { id: '3', code: 'WO-003', operation: 'Welding', jobId: '2' },
  { id: '4', code: 'WO-004', operation: 'Painting', jobId: '2' },
];

const mockResources = [
  { id: '1', name: 'CNC Machine 1', type: 'machine', capacity: 8 },
  { id: '2', name: 'Assembly Line A', type: 'machine', capacity: 16 },
  { id: '3', name: 'John Smith', type: 'operator', capacity: 8 },
  { id: '4', name: 'Jane Doe', type: 'operator', capacity: 8 },
  { id: '5', name: 'Welding Tools', type: 'tool', capacity: 4 },
  { id: '6', name: 'Raw Materials', type: 'material', capacity: 1000 },
];

const mockShifts = [
  { id: '1', name: 'Morning Shift', startTime: '06:00', endTime: '14:00' },
  { id: '2', name: 'Afternoon Shift', startTime: '14:00', endTime: '22:00' },
  { id: '3', name: 'Night Shift', startTime: '22:00', endTime: '06:00' },
];

const mockOperators = [
  { id: '1', name: 'John Smith', skills: ['welding', 'assembly'] },
  { id: '2', name: 'Jane Doe', skills: ['machining', 'quality'] },
  { id: '3', name: 'Bob Johnson', skills: ['painting', 'finishing'] },
  { id: '4', name: 'Alice Brown', skills: ['assembly', 'packaging'] },
];

export function ScheduleForm({ initialData, onSubmit, isLoading = false }: ScheduleFormProps) {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    control,
  } = useForm<ScheduleFormData>({
    resolver: zodResolver(scheduleSchema),
    defaultValues: {
      priority: 'medium',
      status: 'draft',
      resources: [
        {
          resourceId: '',
          resourceType: 'machine',
          quantity: 1,
          allocatedHours: 8,
          startTime: '08:00',
          endTime: '16:00',
        },
      ],
      shifts: [
        {
          shiftId: '',
          date: '',
          startTime: '08:00',
          endTime: '16:00',
          operatorIds: [],
        },
      ],
      constraints: {
        maxConcurrentJobs: 3,
        bufferTime: 30,
        allowOvertime: false,
        requireQualityCheck: true,
      },
      ...initialData,
    },
  });

  const watchedProductionJobId = watch('productionJobId');
  const watchedResources = watch('resources');
  const watchedShifts = watch('shifts');

  const availableWorkOrders = mockWorkOrders.filter(
    wo => wo.jobId === watchedProductionJobId
  );

  const addResource = () => {
    const currentResources = watchedResources || [];
    setValue('resources', [
      ...currentResources,
      {
        resourceId: '',
        resourceType: 'machine',
        quantity: 1,
        allocatedHours: 8,
        startTime: '08:00',
        endTime: '16:00',
      },
    ]);
  };

  const removeResource = (index: number) => {
    const currentResources = watchedResources || [];
    setValue('resources', currentResources.filter((_, i) => i !== index));
  };

  const addShift = () => {
    const currentShifts = watchedShifts || [];
    setValue('shifts', [
      ...currentShifts,
      {
        shiftId: '',
        date: '',
        startTime: '08:00',
        endTime: '16:00',
        operatorIds: [],
      },
    ]);
  };

  const removeShift = (index: number) => {
    const currentShifts = watchedShifts || [];
    setValue('shifts', currentShifts.filter((_, i) => i !== index));
  };

  const handleFormSubmit = (data: ScheduleFormData) => {
    // Validate date range
    const startDate = new Date(data.startDate);
    const endDate = new Date(data.endDate);
    
    if (endDate <= startDate) {
      toast({
        title: 'Invalid Date Range',
        description: 'End date must be after start date',
        variant: 'destructive',
      });
      return;
    }

    // Validate resource allocation
    const totalAllocatedHours = data.resources.reduce((sum, resource) => sum + resource.allocatedHours, 0);
    if (totalAllocatedHours === 0) {
      toast({
        title: 'Invalid Resource Allocation',
        description: 'Total allocated hours must be greater than 0',
        variant: 'destructive',
      });
      return;
    }

    onSubmit(data);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'scheduled': return 'bg-purple-100 text-purple-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      {/* Basic Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Schedule Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="title">Schedule Title *</Label>
              <Input
                id="title"
                {...register('title')}
                placeholder="Enter schedule title"
              />
              {errors.title && (
                <p className="text-sm text-red-600">{errors.title.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="productionJobId">Production Job *</Label>
              <Select
                value={watch('productionJobId')}
                onValueChange={(value) => setValue('productionJobId', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select production job" />
                </SelectTrigger>
                <SelectContent>
                  {mockProductionJobs.map((job) => (
                    <SelectItem key={job.id} value={job.id}>
                      <div className="flex items-center justify-between w-full">
                        <span>{job.name}</span>
                        <Badge className={getStatusColor(job.status)}>
                          {job.status}
                        </Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.productionJobId && (
                <p className="text-sm text-red-600">{errors.productionJobId.message}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              {...register('description')}
              placeholder="Enter schedule description"
              rows={3}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label htmlFor="startDate">Start Date *</Label>
              <Input
                id="startDate"
                type="datetime-local"
                {...register('startDate')}
              />
              {errors.startDate && (
                <p className="text-sm text-red-600">{errors.startDate.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="endDate">End Date *</Label>
              <Input
                id="endDate"
                type="datetime-local"
                {...register('endDate')}
              />
              {errors.endDate && (
                <p className="text-sm text-red-600">{errors.endDate.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="priority">Priority</Label>
              <Select
                value={watch('priority')}
                onValueChange={(value) => setValue('priority', value as any)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={watch('status')}
                onValueChange={(value) => setValue('status', value as any)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Work Orders */}
      {watchedProductionJobId && (
        <Card>
          <CardHeader>
            <CardTitle>Work Orders</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label>Select Work Orders *</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {availableWorkOrders.map((workOrder) => (
                  <div key={workOrder.id} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id={`workOrder-${workOrder.id}`}
                      value={workOrder.id}
                      {...register('workOrderIds')}
                      className="rounded border-gray-300"
                    />
                    <Label htmlFor={`workOrder-${workOrder.id}`} className="text-sm">
                      {workOrder.code} - {workOrder.operation}
                    </Label>
                  </div>
                ))}
              </div>
              {errors.workOrderIds && (
                <p className="text-sm text-red-600">{errors.workOrderIds.message}</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Resource Allocation */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Resource Allocation
            </div>
            <Button type="button" onClick={addResource} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Add Resource
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {watchedResources?.map((resource, index) => (
            <div key={index} className="border rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">Resource {index + 1}</h4>
                {watchedResources.length > 1 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => removeResource(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Resource Type</Label>
                  <Select
                    value={resource.resourceType}
                    onValueChange={(value) => 
                      setValue(`resources.${index}.resourceType`, value as any)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="machine">Machine</SelectItem>
                      <SelectItem value="operator">Operator</SelectItem>
                      <SelectItem value="tool">Tool</SelectItem>
                      <SelectItem value="material">Material</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Resource</Label>
                  <Select
                    value={resource.resourceId}
                    onValueChange={(value) => 
                      setValue(`resources.${index}.resourceId`, value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select resource" />
                    </SelectTrigger>
                    <SelectContent>
                      {mockResources
                        .filter(r => r.type === resource.resourceType)
                        .map((res) => (
                          <SelectItem key={res.id} value={res.id}>
                            {res.name} (Capacity: {res.capacity})
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Quantity</Label>
                  <Input
                    type="number"
                    min="1"
                    {...register(`resources.${index}.quantity`, { valueAsNumber: true })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Allocated Hours</Label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0.1"
                    {...register(`resources.${index}.allocatedHours`, { valueAsNumber: true })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Start Time</Label>
                  <Input
                    type="time"
                    {...register(`resources.${index}.startTime`)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>End Time</Label>
                  <Input
                    type="time"
                    {...register(`resources.${index}.endTime`)}
                  />
                </div>
              </div>
            </div>
          ))}
          {errors.resources && (
            <p className="text-sm text-red-600">{errors.resources.message}</p>
          )}
        </CardContent>
      </Card>

      {/* Shift Planning */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Shift Planning
            </div>
            <Button type="button" onClick={addShift} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Add Shift
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {watchedShifts?.map((shift, index) => (
            <div key={index} className="border rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">Shift {index + 1}</h4>
                {watchedShifts.length > 1 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => removeShift(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Shift</Label>
                  <Select
                    value={shift.shiftId}
                    onValueChange={(value) => 
                      setValue(`shifts.${index}.shiftId`, value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select shift" />
                    </SelectTrigger>
                    <SelectContent>
                      {mockShifts.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.name} ({s.startTime} - {s.endTime})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Date</Label>
                  <Input
                    type="date"
                    {...register(`shifts.${index}.date`)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Start Time</Label>
                  <Input
                    type="time"
                    {...register(`shifts.${index}.startTime`)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>End Time</Label>
                  <Input
                    type="time"
                    {...register(`shifts.${index}.endTime`)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Operators</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {mockOperators.map((operator) => (
                    <div key={operator.id} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={`shift-${index}-operator-${operator.id}`}
                        value={operator.id}
                        {...register(`shifts.${index}.operatorIds`)}
                        className="rounded border-gray-300"
                      />
                      <Label htmlFor={`shift-${index}-operator-${operator.id}`} className="text-sm">
                        {operator.name} ({operator.skills.join(', ')})
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
          {errors.shifts && (
            <p className="text-sm text-red-600">{errors.shifts.message}</p>
          )}
        </CardContent>
      </Card>

      {/* Constraints */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            Schedule Constraints
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="maxConcurrentJobs">Max Concurrent Jobs</Label>
              <Input
                id="maxConcurrentJobs"
                type="number"
                min="1"
                {...register('constraints.maxConcurrentJobs', { valueAsNumber: true })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="bufferTime">Buffer Time (minutes)</Label>
              <Input
                id="bufferTime"
                type="number"
                min="0"
                {...register('constraints.bufferTime', { valueAsNumber: true })}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="allowOvertime"
                {...register('constraints.allowOvertime')}
                className="rounded border-gray-300"
              />
              <Label htmlFor="allowOvertime">Allow Overtime</Label>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="requireQualityCheck"
                {...register('constraints.requireQualityCheck')}
                className="rounded border-gray-300"
              />
              <Label htmlFor="requireQualityCheck">Require Quality Check</Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notes */}
      <Card>
        <CardHeader>
          <CardTitle>Additional Notes</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            {...register('notes')}
            placeholder="Enter any additional notes or special instructions"
            rows={4}
          />
        </CardContent>
      </Card>

      {/* Submit Button */}
      <div className="flex justify-end space-x-4">
        <Button type="button" variant="outline">
          Cancel
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? 'Saving...' : initialData ? 'Update Schedule' : 'Create Schedule'}
        </Button>
      </div>
    </form>
  );
}